import React, { useEffect, useState, useRef } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { axiosInstance } from '../routes/axiosInstance';
import { selectAuthIsAdmin, selectAuthIsMarketOwner, selectAuthIsMarketOwnerReadOnly } from '../slices/authSlice';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';

export default function Changelog() {
  usePageTitle('Changelog');
  useBreadcrumbs([{ label: 'Help' }, { label: 'Changelog' }], 'Recent platform updates and version history');
  const [changelogData, setChangelogData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState('');
  const versionRefs = useRef({});

  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const showTags = isAdmin || isMarketOwner || isMarketOwnerReadOnly;

  useEffect(() => {
    const parseChangelog = (markdown) => {
      const lines = markdown.split('\n');
      const versions = [];
      let currentVersion = null;
      let currentCategory = null;

      lines.forEach((line) => {
        // Version header: ## v0.6.2 • February 3, 2025
        if (line.startsWith('## v')) {
          if (currentVersion) {
            versions.push(currentVersion);
          }
          const match = line.match(/##\s+(v[\d.]+)\s+•\s+(.+)/);
          if (match) {
            currentVersion = {
              version: match[1],
              date: match[2],
              categories: {}
            };
          }
        }
        // Category header: ### Added
        else if (line.startsWith('### ')) {
          currentCategory = line.replace('### ', '').trim();
          if (currentVersion && !currentVersion.categories[currentCategory]) {
            currentVersion.categories[currentCategory] = [];
          }
        }
        // List item: - Something was fixed
        else if (line.startsWith('- ') && currentVersion && currentCategory) {
          const item = line.replace('- ', '').trim();
          if (item) {
            const isAdminOnly = /\*\*admins?-only\*\*/i.test(item);
            if (!isAdminOnly || showTags) {
              currentVersion.categories[currentCategory].push(item);
            }
          }
        }
      });

      if (currentVersion) {
        versions.push(currentVersion);
      }

      return versions;
    };

    const fetchChangelog = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/user/docs/', {
          params: { page: 'changelog' }
        });

        if (response.data && response.data.data && response.data.data.content) {
          const parsed = parseChangelog(response.data.data.content);
          setChangelogData(parsed);
        } else {
          throw new Error('Invalid response structure');
        }
      } catch (err) {
        console.error('Failed to load changelog:', err);
        toast.error('Failed to load changelog.');
        setChangelogData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChangelog();
  }, [showTags]);

  const renderItemText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    if (parts.length === 1) return text;
    return parts.map((part, i) => {
      const match = part.match(/^\*\*([^*]+)\*\*$/);
      if (match) {
        if (!showTags) return null;
        return (
          <span key={i} className="inline-flex items-center rounded border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 mr-1 align-middle">
            {match[1]}
          </span>
        );
      }
      return part;
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Added': 'text-green-600 dark:text-green-400',
      'Fixed': 'text-blue-600 dark:text-blue-400',
      'Changed': 'text-amber-600 dark:text-amber-400',
      'Security': 'text-red-600 dark:text-red-400',
      'Deprecated': 'text-gray-600 dark:text-gray-400',
      'Removed': 'text-gray-600 dark:text-gray-400'
    };
    return colors[category] || 'text-gray-600 dark:text-gray-400';
  };

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
    if (versionRefs.current[version]) {
      const element = versionRefs.current[version];
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading changelog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Version selector */}
      {changelogData.length > 0 && (
        <div className="flex justify-end">
          <div className="w-full sm:w-auto sm:min-w-[240px]">
            <label htmlFor="version-select" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Jump to version
            </label>
            <select
              id="version-select"
              value={selectedVersion}
              onChange={(e) => handleVersionSelect(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-surface dark:bg-gray-870 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            >
              <option value="">Select a version...</option>
              {changelogData.map((version, idx) => (
                <option key={idx} value={version.version}>
                  {version.version} • {version.date}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Versions */}
      <div className="space-y-6 sm:space-y-8">
        {changelogData.map((version, vIdx) => (
          <div 
            key={vIdx} 
            ref={(el) => (versionRefs.current[version.version] = el)}
            className="border-b border-gray-200/70 dark:border-gray-600/70 pb-6 sm:pb-8 last:border-b-0"
          >
            {/* Version Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                {version.version}
              </h2>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="hidden sm:inline text-sm sm:text-base text-gray-500 dark:text-gray-400">•</span>
                <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                  {version.date}
                </span>
                {vIdx === 0 && (
                  <span className="inline-flex items-center rounded-full bg-brand-50 dark:bg-brand-950/40 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300 ring-1 ring-inset ring-brand-200 dark:ring-brand-800">
                    Latest
                  </span>
                )}
              </div>
            </div>

            {/* Categories and Items - Compact Inline Style */}
            <div className="space-y-1.5 sm:space-y-2">
              {Object.entries(version.categories).map(([category, items], cIdx) => (
                items.map((item, iIdx) => (
                  <div key={`${cIdx}-${iIdx}`} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 text-sm sm:text-base">
                    <span className={`font-semibold sm:min-w-[85px] ${getCategoryColor(category)}`}>
                      {category}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 flex-1 pl-3 sm:pl-0">
                      {renderItemText(item)}
                    </span>
                  </div>
                ))
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
